var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { arrow, autoUpdate, flip, FloatingFocusManager, FloatingPortal, hide, offset, safePolygon, shift, size, useClick, useDismiss, useFloating, useFocus, useHover, useInteractions, useRole, } from '@floating-ui/react';
import { useRef } from 'react';
import * as React from 'react';
import { useDirection } from '@creditornot/cb-ingredients';
import { getClosedStyles, usePopoverTransition } from './hooks/usePopoverTransition';
import { usePrevious } from './hooks/usePrevious';
import { PopoverBase } from './PopoverBase';
import { getDefaultGlobalPopoverMountElement } from './utils';
var ARROWUP_WIDTH_PX = 16;
var ARROWUP_HEIGHT_PX = ARROWUP_WIDTH_PX / 2;
var getDirectionAwarePlacement = function (direction, placement) {
    if (direction === 'ltr') {
        return placement;
    }
    if (placement.includes('left')) {
        return placement.replace('left', 'right');
    }
    if (placement.includes('right')) {
        return placement.replace('right', 'left');
    }
    return placement;
};
var defaultPortalElement = getDefaultGlobalPopoverMountElement();
export var Popover = function (_a) {
    var _b;
    var children = _a.children, className = _a.className, content = _a.content, _c = _a.delayOnMouseEnter, delayOnMouseEnter = _c === void 0 ? 50 : _c, delayOnMouseLeave = _a.delayOnMouseLeave, fallbackPlacements = _a.fallbackPlacements, _d = _a.hideWhenReferenceNotInViewport, hideWhenReferenceNotInViewport = _d === void 0 ? true : _d, matchReferenceWidth = _a.matchReferenceWidth, _e = _a.offset, offsetProp = _e === void 0 ? 0 : _e, onCloseStart = _a.onCloseStart, onOpenChange = _a.onOpenChange, onOpenStart = _a.onOpenStart, openProp = _a.open, _f = _a.openOnClick, openOnClick = _f === void 0 ? true : _f, _g = _a.openOnFocus, openOnFocus = _g === void 0 ? false : _g, _h = _a.openOnHover, openOnHover = _h === void 0 ? false : _h, _j = _a.placement, placementProp = _j === void 0 ? 'top' : _j, portalElement = _a.portalElement, _k = _a.positioningStrategy, positioningStrategy = _k === void 0 ? 'absolute' : _k, _l = _a.renderArrow, renderArrow = _l === void 0 ? true : _l, _m = _a.rendering, rendering = _m === void 0 ? 'portal' : _m, _o = _a.role, roleProp = _o === void 0 ? 'dialog' : _o, style = _a.style, _p = _a.zIndex, zIndex = _p === void 0 ? 0 : _p;
    var isControlled = openProp !== undefined;
    var arrowTipRef = useRef(null);
    var direction = useDirection();
    var _q = __read(React.useState(false), 2), openState = _q[0], setOpenState = _q[1];
    var handleOpenChange = function (value) {
        if (!isControlled) {
            setOpenState(value);
        }
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(value);
        if (value) {
            onOpenStart === null || onOpenStart === void 0 ? void 0 : onOpenStart();
        }
        else {
            onCloseStart === null || onCloseStart === void 0 ? void 0 : onCloseStart();
        }
    };
    var close = function () { return handleOpenChange(false); };
    var _r = useFloating({
        open: openProp !== null && openProp !== void 0 ? openProp : openState,
        onOpenChange: handleOpenChange,
        placement: getDirectionAwarePlacement(direction, placementProp),
        middleware: [
            matchReferenceWidth &&
                size({
                    apply: function (_a) {
                        var rects = _a.rects;
                        if (refs.floating.current) {
                            Object.assign(refs.floating.current.style, {
                                width: "".concat(rects.reference.width, "px"),
                            });
                        }
                    },
                }),
            shift(),
            renderArrow && arrow({ element: arrowTipRef, padding: 4 }),
            offset({ mainAxis: (renderArrow ? ARROWUP_HEIGHT_PX : 0) + offsetProp }),
            flip({ fallbackPlacements: fallbackPlacements }),
            hideWhenReferenceNotInViewport && hide(),
        ],
        strategy: positioningStrategy,
        whileElementsMounted: autoUpdate,
    }), context = _r.context, middlewareData = _r.middlewareData, placement = _r.placement, refs = _r.refs, strategy = _r.strategy, x = _r.x, y = _r.y;
    var prevMatchReferenceWidth = usePrevious(matchReferenceWidth);
    // When matchReferenceWidth is set as false from true, the size modifier
    // won't be applied. This code sets the width back to 'auto'.
    if (prevMatchReferenceWidth && !matchReferenceWidth && refs.floating.current) {
        Object.assign(refs.floating.current.style, {
            width: 'auto',
        });
    }
    var _s = usePopoverTransition(context), isMounted = _s.isMounted, styles = _s.styles;
    var placementSide = placement.split('-')[0];
    var click = useClick(context, { enabled: openOnClick });
    var dismiss = useDismiss(context);
    var focus = useFocus(context, { enabled: openOnFocus });
    var hover = useHover(context, {
        enabled: openOnHover,
        delay: { open: delayOnMouseEnter, close: delayOnMouseLeave },
        handleClose: safePolygon(),
    });
    var role = useRole(context, { role: roleProp });
    var _t = useInteractions([
        click,
        dismiss,
        focus,
        hover,
        role,
    ]), getFloatingProps = _t.getFloatingProps, getReferenceProps = _t.getReferenceProps;
    var floatingProps = getFloatingProps();
    var referenceProps = getReferenceProps();
    var popover = isMounted ? (React.createElement(FloatingFocusManager, { closeOnFocusOut: true, context: context, initialFocus: -1, modal: true, order: ['reference', 'content'], returnFocus: true },
        React.createElement(PopoverBase, __assign({}, floatingProps, { arrowPosition: middlewareData.arrow, arrowTipRef: renderArrow ? arrowTipRef : undefined, className: className, ref: refs.setFloating, side: placementSide, style: __assign(__assign(__assign(__assign({}, style), getClosedStyles({ side: placementSide })), styles), { opacity: ((_b = middlewareData.hide) === null || _b === void 0 ? void 0 : _b.referenceHidden) ? 0 : styles.opacity, position: strategy, top: y !== null && y !== void 0 ? y : '0px', left: x !== null && x !== void 0 ? x : '0px', zIndex: zIndex }) }), typeof content === 'function' ? content({ close: close }) : content))) : null;
    return (React.createElement(React.Fragment, null,
        typeof children === 'function'
            ? children({
                refProps: __assign(__assign({}, referenceProps), { ref: refs.setReference }),
            })
            : children,
        rendering === 'portal' && typeof window === 'object' && window.document ? (React.createElement(FloatingPortal, { preserveTabOrder: true, root: portalElement !== null && portalElement !== void 0 ? portalElement : defaultPortalElement }, popover)) : (popover)));
};
//# sourceMappingURL=Popover.js.map